import { Link } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import { BookOpen, FileEdit, FileText, GitFork, Highlighter, Star } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useAuthor } from '@/hooks/useAuthor';
import { sanitizeUrl } from '@/lib/sanitizeUrl';
import { articleSummary, articleTitle } from '@/lib/nip54';
import { publicationSummary, publicationTitle } from '@/lib/nkbip01';
import { targetKindLabel, type ActivityItem as ActivityItemData, type ActivityType } from '@/lib/activity';
import { cn } from '@/lib/utils';

const TYPE_META: Record<ActivityType, { icon: typeof FileText; verb: string; tint: string }> = {
  article: {
    icon: FileText,
    verb: 'published',
    tint: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
  },
  'article-update': {
    icon: FileEdit,
    verb: 'updated',
    tint: 'bg-sky-500/10 text-sky-600 dark:text-sky-400',
  },
  fork: {
    icon: GitFork,
    verb: 'forked',
    tint: 'bg-violet-500/10 text-violet-600 dark:text-violet-400',
  },
  publication: {
    icon: BookOpen,
    verb: 'published a publication',
    tint: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  },
  star: {
    icon: Star,
    verb: 'starred',
    tint: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
  },
  highlight: {
    icon: Highlighter,
    verb: 'highlighted a passage in',
    tint: 'bg-rose-500/10 text-rose-600 dark:text-rose-400',
  },
};

/** Compact relative time: "just now", "5m ago", "2h ago", "3d ago", then a locale date. */
function relativeTime(timestamp: number): string {
  const seconds = Math.max(0, Math.floor(Date.now() / 1000 - timestamp));
  if (seconds < 90) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(timestamp * 1000).toLocaleDateString();
}

/** Avatar + display name linking to the actor's profile. */
function ActorLink({ pubkey, className }: { pubkey: string; className?: string }) {
  const author = useAuthor(pubkey);
  const displayName = author.data?.metadata?.name ?? `${pubkey.slice(0, 8)}…`;
  const picture = sanitizeUrl(author.data?.metadata?.picture);

  return (
    <Link
      to={`/${nip19.npubEncode(pubkey)}`}
      className={cn('inline-flex min-w-0 max-w-full items-center gap-1.5 align-middle hover:underline', className)}
    >
      <Avatar className="size-5 shrink-0">
        <AvatarImage src={picture} alt="" />
        <AvatarFallback>{displayName.charAt(0).toUpperCase()}</AvatarFallback>
      </Avatar>
      <span className="truncate font-medium">{displayName}</span>
    </Link>
  );
}

interface ActivityItemProps {
  item: ActivityItemData;
}

/**
 * One row of the activity feed — deliberately text-first and compact: an
 * icon for the action type, who did it, what they did it to, and when.
 * Long-form context (an article summary, a highlighted passage) gets at
 * most two quiet lines underneath.
 */
export function ActivityItem({ item }: ActivityItemProps) {
  const { event, type } = item;
  const meta = TYPE_META[type];
  const Icon = meta.icon;

  // Where the row's primary link points, and what it says.
  let title: string;
  let href: string | undefined;
  let excerpt: string | undefined;

  if (type === 'star' || type === 'highlight') {
    title = item.target?.title ?? targetKindLabel(item.target?.kind ?? 0);
    href = item.target?.href;
    if (type === 'highlight') excerpt = event.content;
  } else if (type === 'publication') {
    title = publicationTitle(event);
    href = `/p/${encodeURIComponent(event.tags.find(([n]) => n === 'd')?.[1] ?? '')}/${nip19.npubEncode(event.pubkey)}`;
    excerpt = publicationSummary(event);
  } else {
    title = articleTitle(event);
    href = `/w/${encodeURIComponent(event.tags.find(([n]) => n === 'd')?.[1] ?? '')}/${nip19.npubEncode(event.pubkey)}`;
    excerpt = articleSummary(event) ?? event.content.slice(0, 200);
  }

  return (
    <li className="flex gap-3 py-3">
      <span
        className={cn(
          'mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full',
          meta.tint,
        )}
        aria-hidden
      >
        <Icon className={cn('size-4', type === 'star' && 'fill-current')} />
      </span>

      <div className="min-w-0 flex-1">
        <p className="text-sm leading-6 break-words">
          <ActorLink pubkey={event.pubkey} />{' '}
          <span className="text-muted-foreground">{meta.verb}</span>{' '}
          {href ? (
            <Link to={href} className="font-medium hover:underline">
              {title}
            </Link>
          ) : (
            <span className="font-medium">{title}</span>
          )}
          {type === 'fork' && item.forkSourcePubkey && (
            <span className="text-muted-foreground">
              {' '}from <ActorLink pubkey={item.forkSourcePubkey} className="inline-flex" />
            </span>
          )}
          <time
            dateTime={new Date(item.createdAt * 1000).toISOString()}
            title={new Date(item.createdAt * 1000).toLocaleString()}
            className="ml-2 whitespace-nowrap text-xs text-muted-foreground"
          >
            {relativeTime(item.createdAt)}
          </time>
        </p>

        {excerpt && (
          <p
            className={cn(
              'mt-1 text-sm text-muted-foreground break-words',
              type === 'highlight'
                ? 'line-clamp-3 border-l-2 border-border pl-3 italic'
                : 'line-clamp-2',
            )}
          >
            {type === 'highlight' ? `“${excerpt}”` : excerpt}
          </p>
        )}
      </div>
    </li>
  );
}
